import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import {
  isOmrApiConfigured,
  scanOmrLive,
  type LiveOmrScanResult,
} from '@/lib/omr-api';
import { fetchTeacherQuestionSets, type TeacherQuestionSetListItem } from '@/lib/question-sets';
import { useAuth } from '@/providers/auth-provider';

type CaptureHistoryItem = {
  id: string;
  uri: string;
  status: 'success' | 'no_qr' | 'error' | 'processing';
  title: string;
  subtitle: string;
};

export default function OMRScannerScreen() {
  const { setId } = useLocalSearchParams<{ setId: string }>();
  const { profile } = useAuth();
  const router = useRouter();
  const cameraRef = useRef<CameraView | null>(null);
  const [permission, requestPermission] = useCameraPermissions();
  const [result, setResult] = useState<LiveOmrScanResult | null>(null);
  const [scanStep, setScanStep] = useState<'idle' | 'scanning' | 'processing'>('idle');
  const [selectedSet, setSelectedSet] = useState<TeacherQuestionSetListItem | null>(null);
  const [allSets, setAllSets] = useState<TeacherQuestionSetListItem[]>([]);
  const [loadingSet, setLoadingSet] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [scanMessage, setScanMessage] = useState('Center the sheet and keep the QR visible.');
  const [captureHistory, setCaptureHistory] = useState<CaptureHistoryItem[]>([]);

  const getFailureMessage = (payload: LiveOmrScanResult) => {
    const reason = payload.debug?.failure_reason;
    if (reason === 'anchors_missing' || reason === 'anchors_ambiguous') {
      return 'Keep all 4 black corner blocks visible and move the full page into view.';
    }
    if (reason === 'sheet_not_found') {
      return 'Move the full page into view and avoid background edges behind the sheet.';
    }
    if (reason === 'qr_not_found_after_alignment') {
      return 'The sheet aligned, but the QR area is blurred or cropped.';
    }
    return payload.message;
  };

  useEffect(() => {
    async function getSetDetails() {
      if (!profile?.id) {
        setLoadingSet(false);
        return;
      }

      try {
        const sets = await fetchTeacherQuestionSets(profile.id);
        setAllSets(sets);

        if (!setId) {
          setSelectedSet(null);
          return;
        }

        const set = sets.find((item) => item.id === setId);
        if (set) {
          setSelectedSet(set);
        } else {
          Alert.alert('Error', 'Selected MCQ set not found.');
          router.setParams({ setId: undefined });
        }
      } catch (error) {
        console.error('Error fetching set details:', error);
      } finally {
        setLoadingSet(false);
      }
    }

    void getSetDetails();
  }, [profile?.id, router, setId]);

  const handleSelectSet = (nextSetId: string) => {
    setResult(null);
    setScanStep('idle');
    router.setParams({ setId: nextSetId });
  };

  const handleStartScan = async () => {
    if (!selectedSet?.id) {
      Alert.alert('Select a set', 'Pick an MCQ set before scanning.');
      return;
    }

    if (!cameraRef.current || !cameraReady) {
      Alert.alert('Camera not ready', 'Wait a moment and try again.');
      return;
    }

    if (!selectedSet.question_count) {
      Alert.alert(
        'Question count missing',
        'This set does not have any questions yet, so the OMR scanner cannot determine the bubble layout.',
      );
      return;
    }

    if (!isOmrApiConfigured()) {
      Alert.alert(
        'OMR API missing',
        'Set EXPO_PUBLIC_OMR_API_URL in your Expo environment to enable live scanning.',
      );
      return;
    }

    setResult(null);
    setScanMessage('Capturing image...');
    setScanStep('scanning');

    try {
      const capture = await cameraRef.current.takePictureAsync({
        quality: 0.7,
        shutterSound: false,
        skipProcessing: true,
      });
      const captureId = `${Date.now()}`;

      setCaptureHistory((current) => [
        {
          id: captureId,
          uri: capture.uri,
          status: 'processing',
          title: 'Processing',
          subtitle: 'Sending image to server',
        },
        ...current,
      ]);

      setScanStep('processing');
      setScanMessage('Uploading image for OMR processing...');

      const payload = await scanOmrLive({
        imageUri: capture.uri,
        schoolId: selectedSet.school_id,
        questionCount: selectedSet.question_count,
        batchId: selectedSet.id,
      });

      setResult(payload);

      if (payload.status === 'success') {
        setCaptureHistory((current) =>
          current.map((item) =>
            item.id === captureId
              ? {
                  ...item,
                  status: 'success',
                  title: payload.student?.roll ? `Roll ${payload.student.roll}` : 'Scan complete',
                  subtitle: payload.evaluation
                    ? `${payload.evaluation.score}/${payload.evaluation.total} • ${payload.evaluation.accuracy}`
                    : payload.message,
                }
              : item,
          ),
        );
      } else if (payload.status === 'no_qr') {
        setCaptureHistory((current) =>
          current.map((item) =>
            item.id === captureId
              ? {
                  ...item,
                  status: 'no_qr',
                  title: 'QR not found',
                  subtitle: getFailureMessage(payload),
                }
              : item,
          ),
        );
        Alert.alert('QR not found', getFailureMessage(payload));
      } else {
        setCaptureHistory((current) =>
          current.map((item) =>
            item.id === captureId
              ? {
                  ...item,
                  status: 'error',
                  title: 'Scan failed',
                  subtitle: getFailureMessage(payload),
                }
              : item,
          ),
        );
        Alert.alert('Scan failed', getFailureMessage(payload));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Live scan failed.';
      setCaptureHistory((current) =>
        current.map((item, index) =>
          index === 0 && item.status === 'processing'
            ? {
                ...item,
                status: 'error',
                title: 'Upload failed',
                subtitle: message,
              }
            : item,
        ),
      );
      Alert.alert('Scan failed', message);
    } finally {
      setScanMessage('Center the sheet and keep the QR visible.');
      setScanStep('idle');
    }
  };

  const resetScanner = () => {
    setResult(null);
    setScanStep('idle');
    setScanMessage('Center the sheet and keep the QR visible.');
  };

  if (loadingSet || !permission) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#1B5E20" />
        {loadingSet ? <Text style={styles.loadingCopy}>Loading set details...</Text> : null}
      </View>
    );
  }

  if (!setId) {
    return (
      <View style={[styles.container, styles.selectionContainer]}>
        <View style={styles.selectionHeader}>
          <Text style={styles.selectionTitle}>Select MCQ Set</Text>
          <Text style={styles.selectionSub}>Pick a set to begin OMR scanning</Text>
        </View>
        <ScrollView style={styles.selectionScroll} contentContainerStyle={styles.selectionContent}>
          {allSets.map((set) => (
            <Pressable key={set.id} style={styles.setItem} onPress={() => handleSelectSet(set.id)}>
              <View style={styles.setItemIcon}>
                <MaterialIcons name="article" size={20} color="#1B5E20" />
              </View>
              <View style={styles.setItemInfo}>
                <Text style={styles.setItemTitle}>{set.title}</Text>
                <Text style={styles.setItemDept}>{set.class_label} · {set.subject_label}</Text>
              </View>
              <MaterialIcons name="chevron-right" size={20} color="#B5C2B6" />
            </Pressable>
          ))}
          {allSets.length === 0 ? (
            <View style={styles.emptySets}>
              <MaterialIcons name="auto-stories" size={64} color="#E8F5E9" />
              <Text style={styles.emptySetsText}>No MCQ sets found. Create one first.</Text>
              <Pressable style={styles.primaryBtn} onPress={() => router.push('/teacher/sets')}>
                <Text style={styles.btnText}>GO TO SETS</Text>
              </Pressable>
            </View>
          ) : null}
        </ScrollView>
      </View>
    );
  }

  if (!permission.granted) {
    return (
      <View style={[styles.container, styles.permissionContainer]}>
        <View style={styles.permissionBox}>
          <MaterialIcons name="photo-camera" size={64} color="#1B5E20" />
          <Text style={styles.title}>Camera Access</Text>
          <Text style={styles.subtitle}>
            Camera access is required to capture the OMR sheet and send it to the server.
          </Text>
          <Pressable style={styles.primaryBtn} onPress={requestPermission}>
            <Text style={styles.btnText}>GRANT PERMISSION</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CameraView
        ref={cameraRef}
        style={styles.camera}
        facing="back"
        onCameraReady={() => setCameraReady(true)}
      />
      <View style={styles.overlay}>
        <View style={styles.topArea}>
          <View style={styles.topBar}>
            <Pressable style={styles.topIconBtn} onPress={() => router.back()}>
              <MaterialIcons name="arrow-back" size={20} color="white" />
            </Pressable>
            {selectedSet ? (
              <View style={styles.setBadge}>
                <Text style={styles.setLabel} numberOfLines={1}>
                  {selectedSet.title}
                </Text>
              </View>
            ) : (
              <View style={styles.topSpacer} />
            )}
            <Pressable style={styles.topIconBtn} onPress={() => router.setParams({ setId: undefined })}>
              <MaterialIcons name="swap-horiz" size={20} color="white" />
            </Pressable>
          </View>
          <Text style={styles.scanInst}>Align the OMR sheet</Text>
          <Text style={styles.scanSubInst}>{scanMessage}</Text>
        </View>

        <View style={styles.middleArea} />

        {scanStep === 'processing' ? (
          <View style={styles.processingPlate}>
            <ActivityIndicator size="large" color="#1B5E20" />
            <Text style={styles.procText}>PROCESSING IMAGE...</Text>
          </View>
        ) : null}

        <View style={styles.bottomArea}>
          <View style={styles.captureStripWrap}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.captureStrip}
            >
              {captureHistory.length === 0 ? (
                <View style={styles.emptyCaptureCard}>
                  <MaterialIcons name="photo-library" size={18} color="rgba(255,255,255,0.75)" />
                  <Text style={styles.emptyCaptureText}>No photos</Text>
                </View>
              ) : (
                captureHistory.map((item) => (
                  <View key={item.id} style={styles.captureThumbCard}>
                    <Image source={{ uri: item.uri }} style={styles.captureThumb} />
                    <View style={styles.captureStatusBadge}>
                      <Text style={styles.captureStatusText}>{item.status.replace('_', ' ')}</Text>
                    </View>
                  </View>
                ))
              )}
            </ScrollView>
          </View>

          <View style={styles.bottomControls}>
            <View style={styles.bottomSide}>
              {captureHistory[0] ? (
                <View style={styles.latestThumbWrap}>
                  <Image source={{ uri: captureHistory[0].uri }} style={styles.latestThumb} />
                </View>
              ) : (
                <View style={styles.bottomSpacer} />
              )}
            </View>

            <Pressable
              style={[
                styles.scanTrigger,
                (!cameraReady || scanStep !== 'idle') && styles.scanTriggerDisabled,
              ]}
              onPress={() => void handleStartScan()}
              disabled={!cameraReady || scanStep !== 'idle'}
            >
              <View style={styles.triggerOuter}>
                <View style={styles.triggerInner} />
              </View>
            </Pressable>

            <View style={styles.bottomSide}>
              <Pressable style={styles.switchSetBtn} onPress={() => router.setParams({ setId: undefined })}>
                <MaterialIcons name="apps" size={24} color="white" />
              </Pressable>
            </View>
          </View>

          {result ? (
            <View style={styles.resultStrip}>
              <Text style={styles.resultStripText}>
                {result.status === 'success'
                  ? `${result.student?.name ?? 'Unknown'} ${result.evaluation ? `• ${result.evaluation.score}/${result.evaluation.total}` : ''}`
                  : result.message}
              </Text>
              <Pressable onPress={resetScanner}>
                <Text style={styles.resultStripAction}>Clear</Text>
              </Pressable>
            </View>
          ) : (
            <Text style={styles.footerNote}>
              {scanStep === 'idle' ? 'Tap shutter to capture and process.' : 'Processing captured photo.'}
            </Text>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  selectionContainer: {
    backgroundColor: '#F9FBF9',
  },
  permissionContainer: {
    backgroundColor: '#F9FBF9',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
  },
  loadingCopy: {
    marginTop: 10,
    color: '#1B5E20',
    fontWeight: '700',
  },
  camera: {
    flex: 1,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'space-between',
  },
  topArea: {
    paddingTop: 56,
    paddingHorizontal: 16,
  },
  topBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  topIconBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  topSpacer: {
    flex: 1,
  },
  scanInst: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
    marginTop: 14,
    textAlign: 'center',
  },
  scanSubInst: {
    color: 'rgba(255,255,255,0.76)',
    fontSize: 11,
    marginTop: 4,
    textAlign: 'center',
  },
  setBadge: {
    flex: 1,
    marginHorizontal: 12,
    backgroundColor: 'rgba(0,0,0,0.4)',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  setLabel: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
  },
  middleArea: {
    flex: 1,
  },
  processingPlate: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(8,10,9,0.58)',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  procText: {
    color: 'white',
    fontWeight: '800',
    letterSpacing: 1.6,
    fontSize: 12,
  },
  bottomArea: {
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingTop: 14,
    paddingBottom: 28,
  },
  captureStripWrap: {
    minHeight: 86,
    justifyContent: 'center',
  },
  captureStrip: {
    paddingHorizontal: 12,
    gap: 10,
    alignItems: 'center',
  },
  bottomControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    paddingHorizontal: 20,
  },
  captureThumbCard: {
    width: 58,
    height: 74,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#1D2420',
  },
  captureThumb: {
    width: '100%',
    height: '100%',
  },
  captureStatusBadge: {
    position: 'absolute',
    left: 3,
    right: 3,
    bottom: 3,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.68)',
    paddingVertical: 2,
    paddingHorizontal: 4,
  },
  captureStatusText: {
    color: 'white',
    fontSize: 8,
    fontWeight: '600',
    textAlign: 'center',
  },
  emptyCaptureCard: {
    width: 86,
    height: 74,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  emptyCaptureText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 11,
    fontWeight: '500',
  },
  bottomSide: {
    width: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomSpacer: {
    width: 48,
    height: 48,
  },
  latestThumbWrap: {
    width: 48,
    height: 48,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  latestThumb: {
    width: '100%',
    height: '100%',
  },
  switchSetBtn: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  scanTrigger: {
    width: 92,
    height: 92,
    borderRadius: 46,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scanTriggerDisabled: {
    opacity: 0.55,
  },
  triggerOuter: {
    width: 92,
    height: 92,
    borderRadius: 46,
    borderWidth: 4,
    borderColor: 'rgba(255,255,255,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  triggerInner: {
    width: 74,
    height: 74,
    borderRadius: 37,
    backgroundColor: 'white',
  },
  footerNote: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 11,
    fontWeight: '500',
    marginTop: 12,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  permissionBox: {
    flex: 1,
    backgroundColor: 'white',
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  title: {
    fontSize: 26,
    fontWeight: '800',
    color: '#1B5E20',
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 10,
  },
  primaryBtn: {
    backgroundColor: '#1B5E20',
    paddingHorizontal: 30,
    paddingVertical: 16,
    borderRadius: 20,
    marginTop: 10,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  btnText: {
    color: 'white',
    fontWeight: '800',
    letterSpacing: 1,
  },
  latestResultCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingHorizontal: 16,
  },
  resultStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingHorizontal: 16,
  },
  resultStripText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 11,
    flex: 1,
    marginRight: 12,
  },
  resultStripAction: {
    color: 'white',
    fontSize: 11,
    fontWeight: '600',
  },
  selectionHeader: {
    backgroundColor: 'white',
    paddingTop: 60,
    paddingBottom: 24,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EE',
  },
  selectionTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#1B5E20',
  },
  selectionSub: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  selectionScroll: {
    flex: 1,
  },
  selectionContent: {
    padding: 20,
  },
  setItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: 'white',
    borderRadius: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#EDF2ED',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
  },
  setItemIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: '#F1F8F1',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  setItemInfo: {
    flex: 1,
  },
  setItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#2E3D2F',
  },
  setItemDept: {
    fontSize: 12,
    color: '#6A8A6D',
    marginTop: 2,
  },
  emptySets: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 16,
  },
  emptySetsText: {
    fontSize: 15,
    color: '#999',
    textAlign: 'center',
    fontWeight: '600',
  },
});
